/**
 * SourceFilterPills — Shared source-type filter bar
 *
 * Three modes:
 * - tri-state: click=include, right-click/alt+click=exclude, click again=neutral
 * - toggle: click toggles on/off (multi-select)
 * - radio: single selection
 *
 * @module @humanizer/studio/components/ui/SourceFilterPills
 */

import React, { useCallback } from 'react';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export interface SourceInfo {
  sourceType: string;
  count?: number;
  rawTypes?: string[];
}

export type FilterState = Record<string, 'include' | 'exclude'>;

export interface SourceFilterPillsProps {
  /** Available sources to display as pills */
  sources: SourceInfo[];
  /** Current filter state (tri-state map, Set for toggle, or string|null for radio) */
  filters: FilterState;
  /** Called when filters change */
  onFiltersChange: (filters: FilterState) => void;
  /** Interaction mode */
  mode?: 'tri-state' | 'toggle' | 'radio';
  /** Show "All" reset button */
  showAll?: boolean;
  /** Total count to show on "All" button */
  totalCount?: number;
  /** Show count badges on pills */
  showCounts?: boolean;
  /** Show source icons */
  showIcons?: boolean;
  /** Additional class name */
  className?: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// SOURCE ICONS
// ═══════════════════════════════════════════════════════════════════════════

const SOURCE_ICONS: Record<string, string> = {
  chatgpt: '\u{1F916}',
  claude: '\u{1F3AD}',
  facebook: '\u{1F464}',
  reddit: '\u{1F534}',
  substack: '\u{1F4F0}',
  twitter: '\u{1F426}',
  instagram: '\u{1F4F7}',
  midjourney: '\u{1F3A8}',
  'dall-e': '\u{1F5BC}',
  'stable-diffusion': '\u{1F308}',
  'image-collection': '\u{1F4F8}',
  'text-collection': '\u{1F4DD}',
};

export function getSourceIcon(source: string): string {
  return SOURCE_ICONS[source] ?? '\u{1F4C1}';
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export function SourceFilterPills({
  sources,
  filters,
  onFiltersChange,
  mode = 'tri-state',
  showAll = true,
  totalCount,
  showCounts = true,
  showIcons = true,
  className = '',
}: SourceFilterPillsProps): React.ReactElement | null {
  if (sources.length === 0) return null;

  const hasActiveFilters = Object.keys(filters).length > 0;

  const handleClick = useCallback((sourceType: string, e: React.MouseEvent) => {
    onFiltersChange((() => {
      const next = { ...filters };

      if (mode === 'radio') {
        // Radio: click selects, click again deselects
        if (next[sourceType] === 'include') {
          return {};
        }
        return { [sourceType]: 'include' as const };
      }

      if (mode === 'toggle') {
        // Toggle: click adds/removes from include set
        if (next[sourceType]) {
          delete next[sourceType];
        } else {
          next[sourceType] = 'include';
        }
        return next;
      }

      // Tri-state
      if (e.altKey || e.metaKey) {
        if (next[sourceType] === 'exclude') {
          delete next[sourceType];
        } else {
          next[sourceType] = 'exclude';
        }
      } else {
        if (next[sourceType] === 'include') {
          delete next[sourceType];
        } else {
          next[sourceType] = 'include';
        }
      }
      return next;
    })());
  }, [filters, onFiltersChange, mode]);

  const handleContextMenu = useCallback((sourceType: string, e: React.MouseEvent) => {
    if (mode !== 'tri-state') return;
    e.preventDefault();
    onFiltersChange((() => {
      const next = { ...filters };
      if (next[sourceType] === 'exclude') {
        delete next[sourceType];
      } else {
        next[sourceType] = 'exclude';
      }
      return next;
    })());
  }, [filters, onFiltersChange, mode]);

  const getPillClass = (sourceType: string): string => {
    const state = filters[sourceType];
    if (state === 'include') return 'source-filter-pills__pill--include';
    if (state === 'exclude') return 'source-filter-pills__pill--exclude';
    return '';
  };

  const getTitle = (sourceType: string): string => {
    const state = filters[sourceType];
    if (mode === 'tri-state') {
      if (state === 'include') return 'Included (click to clear, right-click to exclude)';
      if (state === 'exclude') return 'Excluded (click to include, right-click to clear)';
      return 'Click to include, right-click to exclude';
    }
    if (mode === 'toggle') {
      return state ? 'Active (click to clear)' : 'Click to filter';
    }
    return state ? 'Selected' : 'Click to select';
  };

  return (
    <div className={`source-filter-pills ${className}`}>
      {showAll && (
        <button
          className={`source-filter-pills__pill ${!hasActiveFilters ? 'source-filter-pills__pill--include' : ''}`}
          onClick={() => onFiltersChange({})}
        >
          All{totalCount != null && totalCount > 0 ? ` ${totalCount}` : ''}
        </button>
      )}
      {sources.map((source) => (
        <button
          key={source.sourceType}
          className={`source-filter-pills__pill ${getPillClass(source.sourceType)}`}
          onClick={(e) => handleClick(source.sourceType, e)}
          onContextMenu={(e) => handleContextMenu(source.sourceType, e)}
          title={getTitle(source.sourceType)}
        >
          {showIcons && (
            <span className="source-filter-pills__icon">{getSourceIcon(source.sourceType)}</span>
          )}
          {source.sourceType}
          {showCounts && source.count != null && (
            <span className="source-filter-pills__count">{source.count}</span>
          )}
        </button>
      ))}
      {mode === 'toggle' && hasActiveFilters && (
        <button
          className="source-filter-pills__pill source-filter-pills__pill--clear"
          onClick={() => onFiltersChange({})}
        >
          Clear
        </button>
      )}
    </div>
  );
}
